import { MenuButtonItem } from 'components/MenuButton';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { createEditUsersModalOptions } from 'modals/modal-options-factory';
import { VIEWS } from 'modals/EditUsersModal/EditUserModal';
import { toggleModal } from 'state/callers';
import Icon from 'components/base/Icon';
import difference from 'lodash/difference';
import {
  LBEditMergedAccountsIcon,
  LBMergeAccountsIcon,
  LBUnmergeAccountsIcon
} from '@linearb/buzz';

const VIEWS_TO_TEXT = {
  [VIEWS.merge]: 'Merge account',
  [VIEWS.unmerge]: 'Unmerge accounts',
  [VIEWS.edit]: 'Edit merged accounts',
  [VIEWS.merge_disabled]: 'Merge account is currently disabled'
};

const sendTrackingEvent = (
  click_type,
  page_tab = TRACKING.PAGE_TABS.INDIVIDUAL,
  props = {}
) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.PEOPLE,
    page_tab,
    click_type,
    ...props
  });
};

export const mergeMenuButtons = ({
  currentContributor,
  onCancel,
  handleSubmitMerge,
  dispatch,
  teamId = null,
  isMergeContributorDisabled
}) => {
  const {
    id,
    contributor_id,
    is_parent: isParent = false,
    contained_ids: childIds = []
  } = currentContributor;
  const currentContributorId = id || contributor_id;

  const parentProps = [
    {
      key: VIEWS_TO_TEXT[VIEWS.unmerge],
      text: VIEWS_TO_TEXT[VIEWS.unmerge],
      icon: <LBUnmergeAccountsIcon />,
      onClick: () => {
        sendTrackingEvent(TRACKING.CLICK.MENU_UNMERGE_USERS);
        dispatch(
          toggleModal(
            createEditUsersModalOptions({
              view: VIEWS.unmerge,
              parentId: currentContributorId,
              connectedContributorIds: childIds,
              onCancel,
              onSubmit: async (primaryId, childIds) => {
                sendTrackingEvent(
                  TRACKING.CLICK.UNMERGE_USER,
                  TRACKING.PAGE_TABS.EDIT_MERGE_USER
                );
                const connectedContributors = childIds.map(id => ({
                  id,
                  parent_id: null,
                  previous_parent_id: primaryId
                }));
                await handleSubmitMerge(
                  currentContributorId,
                  connectedContributors,
                  VIEWS_TO_TEXT[VIEWS.unmerge]
                );
              }
            })
          )
        );
      }
    },
    {
      key: VIEWS_TO_TEXT[VIEWS.edit],
      icon: <LBEditMergedAccountsIcon />,
      text: VIEWS_TO_TEXT[VIEWS.edit],
      onClick: () => {
        sendTrackingEvent(TRACKING.CLICK.MENU_EDIT_USERS);
        dispatch(
          toggleModal(
            createEditUsersModalOptions({
              view: VIEWS.edit,
              parentId: currentContributorId,
              connectedContributorIds: childIds,
              onCancel,
              onSubmit: async (primaryId, remainingChildIds) => {
                const toRemove = difference(childIds, remainingChildIds).map(
                  id => ({ id, parent_id: null, previous_parent_id: primaryId })
                );
                const toAdd = difference(remainingChildIds, childIds).map(
                  id => ({ id, parent_id: primaryId })
                );
                sendTrackingEvent(
                  TRACKING.CLICK.SAVE,
                  TRACKING.PAGE_TABS.EDIT_MERGE_USER,
                  {
                    num_of_merged: toAdd.length,
                    num_of_unmerged: toRemove.length
                  }
                );
                await handleSubmitMerge(
                  currentContributorId,
                  [...toRemove, ...toAdd],
                  VIEWS_TO_TEXT[VIEWS.merge]
                );
              }
            })
          )
        );
      }
    }
  ];
  const nonParentProps = [
    {
      key: VIEWS_TO_TEXT[VIEWS.merge],
      icon: <LBMergeAccountsIcon />,
      text: VIEWS_TO_TEXT[VIEWS.merge],
      onClick: () => {
        sendTrackingEvent(TRACKING.CLICK.MENU_MERGE_USERS);
        dispatch(
          toggleModal(
            createEditUsersModalOptions({
              view: VIEWS.merge,
              parentId: currentContributorId,
              connectedContributorIds: [],
              onCancel,
              onSubmit: async (primaryId, childIds) => {
                sendTrackingEvent(
                  TRACKING.CLICK.SAVE,
                  TRACKING.PAGE_TABS.EDIT_MERGE_USER,
                  {
                    num_of_merged: childIds.length
                  }
                );
                const connectedContributors = childIds.map(id => ({
                  id,
                  parent_id: primaryId,
                  ...(teamId ? { team_id: teamId } : {})
                }));
                await handleSubmitMerge(
                  currentContributorId,
                  connectedContributors,
                  VIEWS_TO_TEXT[VIEWS.merge]
                );
              }
            })
          )
        );
      }
    }
  ];

  const mergeDisabledProps = [
    {
      key: VIEWS_TO_TEXT[VIEWS.merge_disabled],
      icon: <Icon name='UserMerge' color='nepal' />,
      text: VIEWS_TO_TEXT[VIEWS.merge_disabled],
      disabled: true
    }
  ];

  const componentsArr = isMergeContributorDisabled
    ? mergeDisabledProps
    : isParent
    ? parentProps
    : nonParentProps;

  return componentsArr.map(props => (
    <MenuButtonItem key={props.key} {...props} />
  ));
};
